import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function Home() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center z-50">
        <div className="text-center">
          <div className="text-5xl font-light text-white mb-4 animate-pulse tracking-wide">
            AURA BOTANICA
          </div>
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/ec6bd2643d26f341d00609802fe44def.jpg" 
            alt="Hero Background" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto w-full">
          <h1 className="text-5xl md:text-7xl font-light text-white mb-6 tracking-wide">
            AURA BOTANICA
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-4 font-light">
            自然の力で、髪に輝きを
          </p>
          <p className="text-base md:text-lg text-white/80 mb-12 font-light">
            ボタニカルエッセンス配合で、理想のツヤ髪へ
          </p>
          <a 
            href="#products" 
            className="inline-block bg-white text-emerald-700 px-10 py-4 rounded-full text-sm font-medium hover:bg-emerald-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            製品を見る
          </a>
        </div>

        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-3xl text-white"></i>
        </div>
      </section>

      {/* Concept Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="w-full h-[600px]">
                <img 
                  src="https://readdy.ai/api/search-image?query=Natural%20botanical%20ingredients%20including%20fresh%20green%20herbs%2C%20essential%20oil%20bottles%2C%20and%20organic%20plant%20extracts%20arranged%20artistically%20on%20clean%20white%20surface%2C%20soft%20natural%20lighting%2C%20spa%20wellness%20aesthetic%20with%20minimalist%20composition&width=800&height=1000&seq=concept-img-001&orientation=portrait" 
                  alt="Concept" 
                  className="w-full h-full object-cover object-top rounded-lg shadow-xl"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-sm font-semibold text-emerald-600 mb-4 tracking-wider">CONCEPT</p>
              <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6 leading-tight">
                自然と科学の<br />
                完璧な調和
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                AURA BOTANICAは、厳選された植物由来成分と最先端のヘアケア技術を融合させた、次世代のボタニカルヘアケアブランドです。
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                髪と頭皮に優しく働きかけ、ダメージを補修しながら、本来の美しさを引き出します。毎日のケアで、サロン帰りのような仕上がりを実感してください。
              </p>
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-full">
                  <i className="ri-leaf-line text-2xl text-emerald-600"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">100% 植物由来成分</p>
                  <p className="text-xs text-gray-500">自然の恵みをそのまま</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-emerald-600 mb-4 tracking-wider">FEATURES</p>
            <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
              3つの特徴
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-6 mx-auto">
                <i className="ri-plant-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                ボタニカルエッセンス
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed text-center">
                厳選された植物エキスが髪に深く浸透し、内側から潤いを与えます。自然の力で髪本来の美しさを引き出します。
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-6 mx-auto">
                <i className="ri-drop-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                ディープモイスチャー
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed text-center">
                独自の保湿技術により、髪の芯まで潤いを届けます。乾燥によるダメージから髪を守り、しっとりとした質感を実現します。
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-6 mx-auto">
                <i className="ri-sparkling-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                シャインリペア
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed text-center">
                ダメージを補修しながら、自然なツヤを与えます。毛先まで滑らかで、指通りの良い美しい髪へと導きます。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-emerald-600 mb-4 tracking-wider">PRODUCTS</p>
            <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
              製品ラインナップ
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-product-shop>
            {/* Product 1 */}
            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-lg mb-6 bg-gradient-to-br from-emerald-50 to-teal-50">
                <div className="w-full h-[500px]">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Elegant%20emerald%20green%20botanical%20shampoo%20bottle%20with%20minimalist%20design%2C%20clean%20white%20background%2C%20professional%20product%20photography%20with%20soft%20shadows%2C%20luxury%20haircare%20packaging%20with%20natural%20leaf%20motifs%2C%20studio%20lighting%20setup&width=600&height=800&seq=product-shampoo-001&orientation=portrait" 
                    alt="リバイタライジング シャンプー" 
                    className="w-full h-full object-contain object-center p-8 group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-light text-gray-900 mb-2">
                  リバイタライジング シャンプー
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  植物由来の洗浄成分で優しく洗い上げる<br />
                  美容液シャンプー
                </p>
                <p className="text-lg font-semibold text-emerald-600 mb-4">
                  480mL / ¥2,480 (税込)
                </p>
                <button className="inline-flex items-center text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors whitespace-nowrap cursor-pointer">
                  詳しく見る
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>

            {/* Product 2 */}
            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-lg mb-6 bg-gradient-to-br from-emerald-50 to-teal-50">
                <div className="w-full h-[500px]">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Elegant%20teal%20colored%20botanical%20conditioner%20bottle%20with%20minimalist%20design%2C%20clean%20white%20background%2C%20professional%20product%20photography%20with%20soft%20shadows%2C%20luxury%20haircare%20packaging%20with%20natural%20botanical%20elements%2C%20studio%20lighting%20setup&width=600&height=800&seq=product-conditioner-001&orientation=portrait" 
                    alt="ハイドレーティング コンディショナー" 
                    className="w-full h-full object-contain object-center p-8 group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-light text-gray-900 mb-2">
                  ハイドレーティング コンディショナー
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  ダメージを補修し、潤いを閉じ込める<br />
                  美容液ヘアコンディショナー
                </p>
                <p className="text-lg font-semibold text-emerald-600 mb-4">
                  480mL / ¥2,480 (税込)
                </p>
                <button className="inline-flex items-center text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors whitespace-nowrap cursor-pointer">
                  詳しく見る
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ingredients Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-semibold text-emerald-200 mb-4 tracking-wider">INGREDIENTS</p>
              <h2 className="text-4xl md:text-5xl font-light mb-6 leading-tight">
                厳選された<br />
                植物由来成分
              </h2>
              <p className="text-base text-white/90 leading-relaxed mb-8">
                世界中から厳選した植物エキスを贅沢に配合。それぞれの成分が持つ力を最大限に引き出し、髪と頭皮に優しく働きかけます。
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                    <i className="ri-leaf-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">アルガンオイル</h4>
                    <p className="text-sm text-white/80">モロッコ産の希少なオイルが髪に深い潤いを与えます</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                    <i className="ri-leaf-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">カメリアオイル</h4>
                    <p className="text-sm text-white/80">椿油が髪表面を保護し、自然なツヤを与えます</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                    <i className="ri-leaf-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">ローズマリーエキス</h4>
                    <p className="text-sm text-white/80">頭皮環境を整え、健やかな髪の成長をサポート</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="w-full h-[600px]">
                <img 
                  src="https://readdy.ai/api/search-image?query=Collection%20of%20natural%20botanical%20ingredients%20including%20argan%20nuts%2C%20camellia%20flowers%2C%20rosemary%20herbs%2C%20and%20essential%20oils%20arranged%20beautifully%20on%20clean%20surface%2C%20soft%20natural%20lighting%2C%20spa%20wellness%20aesthetic%20with%20organic%20textures%20and%20green%20tones&width=800&height=1000&seq=ingredients-img-001&orientation=portrait" 
                  alt="Ingredients" 
                  className="w-full h-full object-cover object-top rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-emerald-600 mb-4 tracking-wider">NEWS</p>
            <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
              お知らせ
            </h2>
          </div>

          <div className="space-y-6 max-w-4xl mx-auto">
            <a href="#" className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs text-emerald-600 font-semibold mb-2">2024.01.15</p>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    新製品「ボタニカル ヘアオイル」発売のお知らせ
                  </h3>
                  <p className="text-sm text-gray-600">
                    髪に自然なツヤと潤いを与える新製品が登場しました。
                  </p>
                </div>
                <i className="ri-arrow-right-line text-xl text-gray-400 ml-4"></i>
              </div>
            </a>

            <a href="#" className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs text-emerald-600 font-semibold mb-2">2024.01.08</p>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    公式オンラインストアリニューアルのお知らせ
                  </h3>
                  <p className="text-sm text-gray-600">
                    より使いやすく、より便利になりました。
                  </p>
                </div>
                <i className="ri-arrow-right-line text-xl text-gray-400 ml-4"></i>
              </div>
            </a>

            <a href="#" className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs text-emerald-600 font-semibold mb-2">2023.12.20</p>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    年末年始の営業についてのご案内
                  </h3>
                  <p className="text-sm text-gray-600">
                    年末年始の営業時間についてお知らせいたします。
                  </p>
                </div>
                <i className="ri-arrow-right-line text-xl text-gray-400 ml-4"></i>
              </div>
            </a>
          </div>

          <div className="text-center mt-12">
            <button className="inline-flex items-center text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors whitespace-nowrap cursor-pointer">
              すべてのお知らせを見る
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
            自然の力で、<br className="md:hidden" />
            あなたの髪を変える
          </h2>
          <p className="text-base text-gray-600 mb-12 leading-relaxed">
            AURA BOTANICAで、毎日のヘアケアを特別な時間に。<br />
            今すぐ、理想の髪を手に入れましょう。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <a 
              href="#products" 
              className="inline-block bg-emerald-600 text-white px-10 py-4 rounded-full text-sm font-medium hover:bg-emerald-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              製品を購入する
            </a>
            <a 
              href="#" 
              className="inline-block bg-white text-emerald-600 px-10 py-4 rounded-full text-sm font-medium hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              お問い合わせ
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}